/**
 * One Voice - TypeScript Type Definitions
 *
 * This module defines all TypeScript types, interfaces, and enums used
 * throughout the One Voice frontend application. Types mirror the backend
 * API schemas for type-safe data handling.
 *
 * Type Categories:
 *   - Enums: PrayerType, PrayerStatus, UserRole, SubmissionSource
 *   - Prayer Types: PrayerRequest, CreatePrayerRequest, UpdatePrayerRequest
 *   - User Types: User, CreateUser, UpdateUser, LoginCredentials
 *   - API Types: PaginatedResponse, ApiError, AuthResponse
 *   - WebSocket Types: WSEventType, WebSocketMessage
 *   - UI Types: DisplaySettings, QueueItem, ExportOptions
 *
 * Naming Conventions:
 *   - Create*: Data for creating new resources
 *   - Update*: Partial data for updating resources
 *   - *Response: API response shapes
 *
 * @module types
 */

// =============================================================================
// Enumerations
// =============================================================================

/**
 * Type of prayer submission.
 *
 * The backend uses PRAYER_REQUEST/PRAISE_REPORT values, while
 * the frontend UI may use simplified PRAYER/PRAISE values.
 */
export enum PrayerType {
  PRAYER = "prayer",
  PRAISE = "praise",
  // Backend uses these values
  PRAYER_REQUEST = "prayer_request",
  PRAISE_REPORT = "praise_report",
}

/**
 * Prayer request workflow status (moderation and live queue only).
 *
 * Workflow: PENDING -> QUEUED -> DISPLAYED -> ARCHIVED
 * Partner video response state is tracked separately via {@link VideoResponseStatus}.
 *
 * `responded` / `awaiting_response` values are legacy in the API enum; prefer `videoResponseStatus`.
 */
export enum PrayerStatus {
  /** New submission awaiting moderator review */
  PENDING = "pending",
  /** Approved and queued for display (prayer or praise) */
  QUEUED = "queued",
  /** Has been shown on screen during service */
  DISPLAYED = "displayed",
  /** Dismissed (display label); value "archived" in API/DB */
  ARCHIVED = "archived",
  /** @deprecated Legacy; use `videoResponseStatus === 'responded'` */
  RESPONDED = "responded",
  /** @deprecated Legacy; use `videoResponseStatus === 'awaiting'` */
  AWAITING_RESPONSE = "awaiting_response",
}

/**
 * Prayer partner video/text response workflow (independent of {@link PrayerStatus}).
 */
export enum VideoResponseStatus {
  AWAITING = "awaiting",
  RESPONDED = "responded",
}

/**
 * User role for role-based access control (RBAC).
 *
 * Hierarchy (highest to lowest): ADMIN > PRODUCER > MODERATOR > PRAYER_PARTNER > CONGREGANT
 */
export enum UserRole {
  /** Full system access including user management */
  ADMIN = "admin",
  /** Manages live service display queue */
  PRODUCER = "producer",
  /** Reviews and approves prayer submissions */
  MODERATOR = "moderator",
  /** Can view all prayers and respond with text/video (no moderation) */
  PRAYER_PARTNER = "prayer_partner",
  /** Basic user - can submit prayers */
  CONGREGANT = "congregant",
}

/**
 * Channel through which a prayer was submitted.
 */
export enum SubmissionSource {
  /** Web application form */
  WEB = "web",
  /** SMS via Clearstream integration */
  SMS = "sms",
  /** Physical kiosk at church */
  KIOSK = "kiosk",
}

// =============================================================================
// Prayer Request Types
// =============================================================================

/** Submitter gender values collected on the public prayer form. */
export type SubmitterGender = 'male' | 'female' | 'prefer_not_to_say';

/**
 * Complete prayer request data from the API.
 * Includes all fields, some may be null/undefined.
 */
export interface PrayerRequest {
  id: string;
  type: PrayerType;
  body: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  gender: SubmitterGender | null;
  location: string | null;
  phone: string | null;
  isAnonymous: boolean;
  /** Primary category (first of categories, or null). */
  category: string | null;
  /** Selected category tags (source of truth when present). */
  categories?: string[];
  status: PrayerStatus;
  /** Partner video workflow; orthogonal to `status` / live queue */
  videoResponseStatus?: VideoResponseStatus | null;
  submittedVia: SubmissionSource;
  createdAt: string;
  updatedAt: string;
  // Optional fields for moderation
  moderatedBy?: string;
  moderatedAt?: string;
  rejectionReason?: string;
  // Optional fields for display
  displayedAt?: string;
  displayCount?: number;
  /** True when created by the practice simulation (admin dashboard). */
  isSimulation?: boolean;
  /** Moderator inappropriate-content flags (card stays in queue). */
  reportCount?: number;
  /** True when the prayer is currently on the stage tablet queue. */
  onStage?: boolean;
}

/**
 * Data for creating a new prayer request.
 * Web submissions require body, email, and gender.
 */
export interface CreatePrayerRequest {
  type: PrayerType;
  body: string;
  firstName?: string;
  lastName?: string;
  email: string;
  gender: SubmitterGender;
  location?: string;
  phone?: string;
  isAnonymous?: boolean;
  category?: string;
  /** Client device ID for moderation tracking (sent as device_id). */
  deviceId?: string;
}

/**
 * Partial data for updating a prayer request.
 * All fields optional - only provided fields are updated.
 */
export interface UpdatePrayerRequest {
  body?: string;
  firstName?: string;
  lastName?: string;
  location?: string;
  /** Multi-tag override (preferred). */
  categories?: string[];
  /** Legacy single category; prefer categories. */
  category?: string;
  type?: PrayerType;
  status?: PrayerStatus;
  rejectionReason?: string;
}

// =============================================================================
// User Types
// =============================================================================

/**
 * User account data from the API.
 */
/** How often a responder receives the pending prayer-response digest email. */
export type PendingDigestFrequency = 'off' | 'daily' | 'weekly';

export interface User {
  id: string;
  email: string;
  role: UserRole;
  firstName: string | null;
  lastName: string | null;
  isActive: boolean;
  canRespondVideo?: boolean;
  canRespondAudio?: boolean;
  pendingDigestFrequency?: PendingDigestFrequency;
  recordingConsentRequired?: boolean;
  recordingConsentType?: 'video' | 'audio' | 'either' | null;
  recordingConsentRevoked?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUser {
  email: string;
  password: string;
  role: UserRole;
  firstName: string;
  lastName: string;
  canRespondVideo?: boolean;
  canRespondAudio?: boolean;
}

export interface UpdateUser {
  email?: string;
  password?: string;
  role?: UserRole;
  firstName?: string;
  lastName?: string;
  isActive?: boolean;
  canRespondVideo?: boolean;
  canRespondAudio?: boolean;
}

/** Payload for inviting a user by email (they set their own password via link). */
export interface InviteUser {
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  canRespondVideo?: boolean;
  canRespondAudio?: boolean;
}

/** 503 response when invite was created but email could not be sent. */
export interface InviteUserErrorBody {
  detail: string;
  inviteLink?: string;
}

// =============================================================================
// Authentication Types
// =============================================================================

/**
 * Credentials for login request.
 */
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  // Backend returns snake_case, but frontend transforms to camelCase
  // Support both formats for compatibility
  accessToken?: string;  // camelCase (after transformation)
  access_token?: string; // snake_case (original from backend)
  tokenType?: string;
  token_type?: string;
  user: User;
}

// =============================================================================
// API Response Types
// =============================================================================

/**
 * Generic paginated response from list endpoints.
 *
 * @template T - Type of items in the list
 */
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ApiError {
  detail: string;
  status: number;
}

// =============================================================================
// WebSocket Types
// =============================================================================

/**
 * WebSocket event types for real-time updates.
 * Used to identify the type of message received from the server.
 */
export enum WSEventType {
  // Prayer events
  NEW_PRAYER = 'new_prayer',
  PRAYER_UPDATED = 'prayer_updated',
  PRAYER_DISMISSED = 'prayer_dismissed',
  PRAYER_FLAGGED = 'prayer_flagged',
  PRAYER_ANSWERED = 'prayer_answered',
  PRAYER_APPROVED = 'prayer_approved',
  
  // Queue events
  PRAYER_QUEUED = 'prayer_queued',
  /** Minimal hint to public /pray clients; staff use PRAYER_QUEUED with full row */
  LIVE_QUEUE_ITEM_ADDED_PUBLIC = 'live_queue_item_added_public',
  PRAYER_UNQUEUED = 'prayer_unqueued',
  QUEUE_REORDERED = 'queue_reordered',
  QUEUE_CLEARED = 'queue_cleared',
  
  // Display events
  PRAYER_DISPLAYED = 'prayer_displayed',
  DISPLAY_CLEARED = 'display_cleared',
  SHUFFLE_STATE_CHANGED = 'shuffle_state_changed',

  // Stage tablet queue
  STAGE_ITEM_ADDED = 'stage_item_added',
  STAGE_ITEM_REMOVED = 'stage_item_removed',
  STAGE_QUEUE_CLEARED = 'stage_queue_cleared',

  // Editing presence (moderators, producers, admin)
  PRAYER_EDITING_STARTED = 'prayer_editing_started',
  PRAYER_EDITING_STOPPED = 'prayer_editing_stopped',

  // Responding presence (moderators, producers, admin)
  PRAYER_RESPONDING_STARTED = 'prayer_responding_started',
  PRAYER_RESPONDING_STOPPED = 'prayer_responding_stopped',

  // System events
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  ERROR = 'error',
  PONG = 'pong',
  /** Server is restarting; client should reconnect with fresh backoff */
  SERVER_SHUTDOWN = 'server_shutdown',
  ROOMS_UPDATED = 'rooms_updated',
  STATUS = 'status',
}

// WebSocket message types
export interface WebSocketMessage<T = unknown> {
  event: WSEventType | string;
  data: T;
}

// Legacy type alias for backwards compatibility
export interface LegacyWebSocketMessage {
  type: "new_prayer" | "status_update" | "display_update" | "connection";
  payload: unknown;
}

// =============================================================================
// UI/Display Types
// =============================================================================

/**
 * Prayer category for filtering and display.
 */
export interface Category {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  sortOrder: number;
}

/**
 * Settings for prayer display during services.
 */
export interface DisplaySettings {
  autoAdvance: boolean;
  displayDuration: number; // seconds
  transitionType: "fade" | "slide" | "none";
  showCategory: boolean;
  showLocation: boolean;
  fontSize: "small" | "medium" | "large";
  theme: "light" | "dark";
}

/**
 * An item in the producer's display queue.
 */
export interface QueueItem {
  id: string;
  prayerRequestId: string;
  prayerRequest: PrayerRequest;
  position: number;
  queueType: "prayer" | "praise";
  createdAt: string;
  displayedAt: string | null;
  isDisplayed: boolean;
}

/**
 * Queue items organized by category for grouped display.
 */
export interface GroupedQueueItems {
  [category: string]: QueueItem[];
}

/**
 * Options for exporting queue to ProPresenter format.
 */
export interface ExportOptions {
  groupByCategory?: boolean;
  includeCategoryLabels?: boolean;
}
